import { useEffect, useState } from 'react';

/**
 * `atriumRedesign` feature flag (Sprint 82, ADR-082).
 *
 * Gates whether the new Atrium two-tier shell renders instead of the existing
 * `DashboardSidebar` shell. Default OFF.
 *
 * WHY A CLIENT TOGGLE (not a per-condo `CondoCorpFeature` flag):
 * The codebase's customer-facing flags live in `condo.condo_corp_features` and
 * are read via `isFeatureActive(...)` (see `Payments.tsx`). Those gate *licensed
 * capabilities* per condo. Atrium is NOT a licensed capability — it's a
 * cross-cutting UI rollout switch used during the 82–87 redesign program to let
 * the new and old shells coexist until per-role parity (ADR-082). Per
 * `docs/architecture/feature-flag-conventions.md` ("What flags are NOT for"),
 * rollout/build-time switches do not belong in the per-condo table. So we use a
 * lightweight client toggle:
 *
 *   - default                                          → ON (post-parity rollout)
 *   - `localStorage.setItem('atriumRedesign', 'off')`  → persistent opt-OUT (legacy)
 *   - `?atrium=0` query param                          → one-off legacy preview
 *   - `?atrium=1` query param                          → force on (overrides LS 'off')
 *
 * Sprint 90 (ADR-082/084 close-out): the redesign reached per-role parity and
 * merged to main, so the default is now ON for everyone. The toggle survives
 * only as an escape hatch to the legacy shell (`?atrium=0` or LS 'off') and can
 * be retired once the legacy `DashboardSidebar` shell is removed.
 */
export const ATRIUM_FLAG_KEY = 'atriumRedesign';

export function isAtriumRedesignEnabled(): boolean {
  if (typeof window === 'undefined') return false;

  // Query param wins (explicit per-request override).
  try {
    const param = new URLSearchParams(window.location.search).get('atrium');
    if (param === '1' || param === 'on') return true;
    if (param === '0' || param === 'off') return false;
  } catch {
    /* malformed URL — fall through to localStorage */
  }

  try {
    // Default ON; honour an explicit persistent opt-out to the legacy shell.
    return window.localStorage.getItem(ATRIUM_FLAG_KEY) !== 'off';
  } catch {
    return true; // storage blocked (private mode) — still default to Atrium
  }
}

/** React hook: re-reads the flag on mount and on storage changes across tabs. */
export function useAtriumRedesign(): boolean {
  const [enabled, setEnabled] = useState<boolean>(isAtriumRedesignEnabled);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === ATRIUM_FLAG_KEY) setEnabled(isAtriumRedesignEnabled());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return enabled;
}
